import { memo, useCallback, useMemo, useState } from 'react';
import { CellRendererProps, ListRenderItem, View } from 'react-native';
import { useTranslation } from '@betty/i18n';
import { useNavigation } from '@betty/navigation';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import {
  Box,
  Button,
  Icon,
  List,
  Lottie,
  Pagination,
  Text,
  usePagination,
} from '@betty/moonlight';
import { formatNumber } from '../../utils/number';
import {
  getRewardsAmount,
  getJackpotReward,
  getJackpotMilestone,
  getMilestoneWinners,
} from './CommunityJackpotClaim.utils';
import {
  claimCommunityEventRewards,
  selectCommunityJackpotEvents,
  selectCommunityJackpotRewards,
} from '../../state';
import {
  STYLE,
  CONTENT_CONTAINER_STYLE,
} from './CommunityJackpotClaim.content';
import { PlayerCommunityEventReward } from '../../state/communityJackpot.types';
import { CommunityJackpotClaimProps } from './CommunityJackpotClaim.types';
import CommunityJackpotImage from '../CommunityJackpotImage/CommunityJackpotImage';
import Flare from '../CommunityJackpot/JackpotProgress/JackpotProgressBar/Flare/Flare';

function CommunityJackpotClaim(props: CommunityJackpotClaimProps) {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const { t } = useTranslation('communityJackpot');
  const {
    layout,
    listRef,
    onViewableItemsChanged,
    onLayout,
    onScroll,
    ...rest
  } = usePagination();
  const [loading, setLoading] = useState(false);
  const [event] = useSelector(selectCommunityJackpotEvents);
  const rewards = useSelector(selectCommunityJackpotRewards);
  const milestoneWinners = useMemo(() => getMilestoneWinners(event), [event]);
  const rewardsAmount = useMemo(() => getRewardsAmount(rewards), [rewards]);
  const jackpotReward = useMemo(() => getJackpotReward(rewards), [rewards]);
  const jackpotMilestone = useMemo(
    () => getJackpotMilestone(event, jackpotReward),
    [event, jackpotReward],
  );
  const { rewardWonUrl, jackpotWonUrl } = event;

  const rewardIcon = (
    <Icon
      name="bettyCoin"
      gradient="accentBackground"
      width="space-500"
      height="space-500"
    />
  );

  const separator = {
    gradient: <Box variant="gradient" gradient="accentBackground" height={1} />,
    emphasis: (
      <Box variant="outline-gradient" gradient="borderEmphasis" height={1} />
    ),
  };

  const renderCell = useCallback(
    ({ children, style, ...props }: CellRendererProps<unknown>) => (
      <View style={[style, { flex: 1 }]} {...props}>
        {children}
      </View>
    ),
    [],
  );

  const renderItem = useCallback<ListRenderItem<PlayerCommunityEventReward>>(
    ({ item }) => {
      return (
        <Box
          flex={1}
          gap="space-400"
          paddingTop="space-800"
          minWidth={layout.width}
          centerContent
        >
          <Box flex={1} width="100%" centerContent>
            <CommunityJackpotImage
              url={
                item.rewardType == 'CommunityEventJackpotReward'
                  ? jackpotWonUrl
                  : rewardWonUrl
              }
              fallback={
                item.rewardType == 'CommunityEventJackpotReward'
                  ? 'communityJackpot.jackpotBag'
                  : 'common.BettyCoinStack'
              }
              width="100%"
              height="100%"
              maxWidth={300}
              maxHeight={300}
            />
          </Box>
          {rewards.length > 1 && (
            <Box>
              <Text
                variant="overline"
                locKey="milestoneHitTitle"
                locNS="communityJackpot"
                color="darkSurfacePrimary"
                textAlign="center"
              />
              {item.rewardType == 'CommunityEventJackpotReward' ? (
                <Text
                  variant="h6"
                  color="darkSurfacePrimary"
                  paddingTop="space-100"
                  textAlign="center"
                >
                  {t('claimJackpotRewardTitle', {
                    label:
                      jackpotMilestone?.label ||
                      t('milestoneJackpot').toLowerCase(),
                  })}
                </Text>
              ) : null}
              <Box alignSelf="center" marginTop="space-600">
                {separator.emphasis}
                <Box paddingHorizontal="space-600" paddingVertical="space-200">
                  <Text
                    variant="badge"
                    locKey="yourReward"
                    locNS="communityJackpot"
                    color="darkSurfacePrimary"
                    paddingBottom="space-100"
                    textAlign="center"
                  />
                  <Box flexDirection="row" gap="space-100" centerContent>
                    <Icon
                      name="bettyCoin"
                      gradient="accentBackground"
                      width="space-500"
                      height="space-500"
                    />
                    <Text variant="h4" gradient="darkSurfaceAccent">
                      {formatNumber(item.rewardAmount, ',')}
                    </Text>
                  </Box>
                </Box>
                {separator.emphasis}
              </Box>
            </Box>
          )}
        </Box>
      );
    },
    [
      jackpotMilestone?.label,
      jackpotWonUrl,
      rewardWonUrl,
      rewards.length,
      separator.emphasis,
      layout.width,
      t,
    ],
  );

  const onCollectRewards = useCallback(async () => {
    setLoading(true);

    const response = await dispatch(claimCommunityEventRewards());

    setLoading(false);

    if (response.meta.requestStatus === 'fulfilled') {
      navigation.popToTop();
      navigation.goBack();

      Lottie.present({ animation: 'confetti' });
    }
  }, [navigation, dispatch]);

  return (
    <Box gap="space-400" {...props}>
      <Text
        variant="overline"
        gradient="darkSurfaceAccent"
        paddingHorizontal="space-600"
        textAlign="center"
      >
        {rewards.length === 1 && jackpotReward
          ? t('claimJackpotRewardTitle', {
              label: jackpotMilestone?.label || t('milestoneJackpot'),
            })
          : t('claimRewardTitle', {
              count: rewards.length,
            })}
      </Text>
      <Text
        variant="h1"
        gradient="darkSurfaceAccent"
        paddingHorizontal="space-600"
        textAlign="center"
      >
        {formatNumber(rewardsAmount, ',')}
      </Text>
      <Box paddingHorizontal="space-600">
        {separator.gradient}
        <Box
          gap="space-300"
          alignItems="center"
          justifyContent="center"
          flexDirection="row"
          paddingVertical="space-200"
        >
          {rewardIcon}
          <Text
            variant="callout"
            locKey="bettyCoins"
            locNS="communityJackpot"
            gradient="darkSurfaceAccent"
          />
          {rewardIcon}
        </Box>
        {separator.gradient}
      </Box>
      <Box flex={1} overflow="hidden" centerContent>
        <Flare variant="big" />
        <List
          ref={listRef}
          data={rewards}
          contentContainerStyle={CONTENT_CONTAINER_STYLE}
          style={STYLE}
          showsHorizontalScrollIndicator={false}
          onViewableItemsChanged={onViewableItemsChanged}
          onScroll={onScroll}
          CellRendererComponent={renderCell}
          renderItem={renderItem}
          onLayout={onLayout}
          pagingEnabled
          horizontal
        />
        {rewards.length > 1 && (
          <Pagination data={rewards} marginTop="space-400" {...rest} />
        )}
      </Box>
      <Box paddingHorizontal="space-400" gap="space-400">
        <Text variant="caption" color="darkSurfacePrimary" textAlign="center">
          {t('playersWon', {
            count: milestoneWinners,
          })}
        </Text>
        <Button
          locKey="collectRewards"
          locNS="communityJackpot"
          size="large"
          loading={loading}
          onPress={onCollectRewards}
        />
      </Box>
    </Box>
  );
}

export default memo(CommunityJackpotClaim);
